import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "./index.css";
import { AuthProvider, useAuth } from "@/lib/auth.jsx";

import PublicLayout from "@/components/layout/PublicLayout";
import Home from "@/pages/Home";
import FindCare from "@/pages/FindCare";
import Doctors from "@/pages/Doctors";
import DoctorProfile from "@/pages/DoctorProfile";
import Specialists from "@/pages/Specialists";
import HowItWorks from "@/pages/HowItWorks";
import Pricing from "@/pages/Pricing";
import TrustSafety from "@/pages/TrustSafety";
import About from "@/pages/About";
import ForDoctors from "@/pages/ForDoctors";
import ForPatients from "@/pages/ForPatients";
import FAQ from "@/pages/FAQ";
import Contact from "@/pages/Contact";
import HealthInfo from "@/pages/HealthInfo";
import HelpCentre from "@/pages/HelpCentre";
import Legal from "@/pages/Legal";

import Login from "@/pages/auth/Login";
import RegisterPatient from "@/pages/auth/RegisterPatient";
import RegisterDoctor from "@/pages/auth/RegisterDoctor";

import PatientLayout from "@/components/layout/PatientLayout";
import PatientDashboard from "@/pages/patient/Dashboard";
import AIIntake from "@/pages/patient/AIIntake";
import BookAppointment from "@/pages/patient/BookAppointment";
import PatientAppointments from "@/pages/patient/Appointments";
import PaymentSuccess from "@/pages/patient/PaymentSuccess";
import PaymentCancel from "@/pages/patient/PaymentCancel";
import CarePassport from "@/pages/patient/CarePassport";
import VideoRoom from "@/pages/patient/VideoRoom";
import PatientMessages from "@/pages/patient/Messages";

import DoctorLayout from "@/components/layout/DoctorLayout";
import DoctorDashboard from "@/pages/doctor/Dashboard";
import DoctorAppointments from "@/pages/doctor/Appointments";
import DoctorAvailability from "@/pages/doctor/Availability";
import DoctorEarnings from "@/pages/doctor/Earnings";
import DoctorConsultation from "@/pages/doctor/Consultation";
import DoctorVerification from "@/pages/doctor/Verification";
import DoctorMessages from "@/pages/doctor/Messages";

import AdminLayout from "@/components/layout/AdminLayout";
import AdminDashboard from "@/pages/admin/Dashboard";
import AdminDoctors from "@/pages/admin/Doctors";
import AdminPatients from "@/pages/admin/Patients";
import AdminAppointments from "@/pages/admin/Appointments";
import AdminSettings from "@/pages/admin/Settings";
import AdminAudit from "@/pages/admin/Audit";

const RequireRole = ({ role, children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (Array.isArray(role) ? !role.includes(user.role) : user.role !== role)
    return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/find-care" element={<FindCare />} />
            <Route path="/doctors" element={<Doctors />} />
            <Route path="/doctors/:id" element={<DoctorProfile />} />
            <Route path="/specialists" element={<Specialists />} />
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/trust-safety" element={<TrustSafety />} />
            <Route path="/about" element={<About />} />
            <Route path="/for-doctors" element={<ForDoctors />} />
            <Route path="/for-patients" element={<ForPatients />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/health-info" element={<HealthInfo />} />
            <Route path="/help" element={<HelpCentre />} />
            <Route path="/legal/:page" element={<Legal />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<RegisterPatient />} />
            <Route path="/register/doctor" element={<RegisterDoctor />} />
          </Route>

          <Route path="/patient" element={<RequireRole role="patient"><PatientLayout /></RequireRole>}>
            <Route index element={<PatientDashboard />} />
            <Route path="dashboard" element={<PatientDashboard />} />
            <Route path="intake" element={<AIIntake />} />
            <Route path="book/:doctorId" element={<BookAppointment />} />
            <Route path="appointments" element={<PatientAppointments />} />
            <Route path="care-passport" element={<CarePassport />} />
            <Route path="video/:apptId" element={<VideoRoom />} />
            <Route path="messages" element={<PatientMessages />} />
            <Route path="payment/success" element={<PaymentSuccess />} />
            <Route path="payment/cancel" element={<PaymentCancel />} />
          </Route>

          <Route path="/doctor" element={<RequireRole role="doctor"><DoctorLayout /></RequireRole>}>
            <Route index element={<DoctorDashboard />} />
            <Route path="dashboard" element={<DoctorDashboard />} />
            <Route path="appointments" element={<DoctorAppointments />} />
            <Route path="availability" element={<DoctorAvailability />} />
            <Route path="earnings" element={<DoctorEarnings />} />
            <Route path="consultation/:apptId" element={<DoctorConsultation />} />
            <Route path="verification" element={<DoctorVerification />} />
            <Route path="messages" element={<DoctorMessages />} />
            <Route path="video/:apptId" element={<VideoRoom />} />
          </Route>

          <Route path="/admin" element={<RequireRole role="admin"><AdminLayout /></RequireRole>}>
            <Route index element={<AdminDashboard />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="doctors" element={<AdminDoctors />} />
            <Route path="patients" element={<AdminPatients />} />
            <Route path="appointments" element={<AdminAppointments />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="audit" element={<AdminAudit />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
